import React from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Plus, Clock, MapPin, CheckCircle2, Circle,
  Trash2, Edit, ListTodo, Users, CalendarClock, Repeat
} from "lucide-react";
import { PRIORITY_CONFIG, formatTime, formatShortDate } from "./pulseUtils";

export const PulseDayPanel = ({
  selectedDate, selectedDateEvents, tasks, taskFilter,
  onTaskFilterChange, onOpenEventDialog, onOpenTaskDialog,
  onDeleteEvent, onToggleTaskComplete, onDeleteTask,
  onConvertToTeamTask, onEditEvent, onEditTask
}) => {
  const { t, i18n } = useTranslation();
  // Map JS Date weekday/month to localized labels
  const weekdayKeys = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
  const weekdayKey = weekdayKeys[selectedDate.getDay()];
  const weekdayLabel = t(`pulsePlanner.daysShort.${weekdayKey}`);
  const monthLabel = t(`pulsePlanner.months.${selectedDate.getMonth() + 1}`);

  const priorityLabel = (p) => t(`pulsePlanner.${p || 'medium'}`);

  return (
    <div className="space-y-4">
      {/* Selected Date Header */}
      <div className="bg-white border border-slate-200 rounded-sm p-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-indigo-600 rounded-sm flex items-center justify-center">
            <span className="text-white font-mono font-bold text-lg">{selectedDate.getDate()}</span>
          </div>
          <div>
            <p className="font-semibold text-slate-900">
              {weekdayLabel}
            </p>
            <p className="text-xs text-slate-500">
              {monthLabel} {selectedDate.getFullYear()}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => onOpenTaskDialog()} className="rounded-sm h-8">
            <ListTodo className="w-3 h-3 mr-1" />{t("pulsePlanner.task")}
          </Button>
          <Button size="sm" onClick={() => onOpenEventDialog(null, selectedDate)} className="rounded-sm h-8 bg-indigo-600 hover:bg-indigo-700">
            <Plus className="w-3 h-3 mr-1" />{t("pulsePlanner.event")}
          </Button>
        </div>
      </div>

      {/* Events Section */}
      <div className="bg-white border border-slate-200 rounded-sm">
        <div className="px-4 py-2 border-b border-slate-100 flex items-center gap-2">
          <CalendarClock className="w-4 h-4 text-indigo-600" />
          <h3 className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{t("pulsePlanner.events")}</h3>
          <span className="text-xs text-slate-400">({selectedDateEvents.length})</span>
        </div>
        <div className="p-2 space-y-2 max-h-[180px] overflow-y-auto">
          {selectedDateEvents.length === 0 ? (
            <p className="text-xs text-slate-400 text-center py-4">{t("pulsePlanner.noEventsForDay")}</p>
          ) : (
            selectedDateEvents.map(event => (
              <div key={event.id} className="p-3 bg-slate-50 rounded-sm border-l-3 border-l-indigo-600 hover:bg-slate-100 transition-colors group">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 text-sm truncate">{event.title}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-indigo-600 font-mono">
                        {formatTime(event.start_datetime)} - {formatTime(event.end_datetime)}
                      </span>
                      {event.repeat_rule && (
                        <span className="text-xs text-indigo-400 flex items-center gap-0.5" title={t("pulsePlanner.repeats", { rule: t(`pulsePlanner.${event.repeat_rule}`, event.repeat_rule) })}>
                          <Repeat className="w-3 h-3" />{t(`pulsePlanner.${event.repeat_rule}`, event.repeat_rule)}
                        </span>
                      )}
                      {event.location && (
                        <span className="text-xs text-slate-500 flex items-center gap-1">
                          <MapPin className="w-3 h-3" />{event.location}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button variant="ghost" size="sm" onClick={() => onEditEvent(event)} className="h-7 w-7 p-0">
                      <Edit className="w-3 h-3" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => onDeleteEvent(event.id)} className="h-7 w-7 p-0 text-red-600 hover:text-red-700">
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Tasks Section */}
      <div className="bg-white border border-slate-200 rounded-sm">
        <div className="px-4 py-2 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ListTodo className="w-4 h-4 text-blue-600" />
            <h3 className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{t("pulsePlanner.tasks")}</h3>
            <span className="text-xs text-slate-400">({tasks.length})</span>
          </div>
          <Select value={taskFilter} onValueChange={onTaskFilterChange}>
            <SelectTrigger className="h-7 w-24 text-xs rounded-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="open">{t("pulsePlanner.filterOpen")}</SelectItem>
              <SelectItem value="today">{t("pulsePlanner.filterToday")}</SelectItem>
              <SelectItem value="upcoming">{t("pulsePlanner.filterUpcoming")}</SelectItem>
              <SelectItem value="overdue">{t("pulsePlanner.filterOverdue")}</SelectItem>
              <SelectItem value="completed">{t("pulsePlanner.filterCompleted")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="p-2 space-y-2 max-h-[250px] overflow-y-auto">
          {tasks.length === 0 ? (
            <p className="text-xs text-slate-400 text-center py-4">{t("pulsePlanner.noTasksFound")}</p>
          ) : (
            tasks.map(task => (
              <div
                key={task.id}
                data-testid={`task-${task.id}`}
                className={`p-3 rounded-sm border-l-3 transition-colors group ${
                  task.priority === 'high' ? 'bg-red-50/50 border-l-red-500' :
                  task.priority === 'medium' ? 'bg-amber-50/50 border-l-amber-500' :
                  'bg-emerald-50/50 border-l-emerald-500'
                } ${task.status === 'completed' ? 'opacity-60' : ''}`}
              >
                <div className="flex items-start gap-2">
                  <button
                    onClick={() => onToggleTaskComplete(task)}
                    className="mt-0.5 flex-shrink-0"
                    data-testid={`task-toggle-${task.id}`}
                  >
                    {task.status === "completed" ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    ) : (
                      <Circle className="w-4 h-4 text-slate-300 hover:text-indigo-600" />
                    )}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className={`font-medium text-sm truncate ${task.status === "completed" ? "text-slate-400 line-through" : "text-slate-900"}`}>
                        {task.title}
                      </p>
                      <Badge variant="outline" className={`text-[9px] h-4 ${PRIORITY_CONFIG[task.priority]?.color}`}>
                        {priorityLabel(task.priority)}
                      </Badge>
                    </div>
                    {task.due_datetime && (
                      <p className="text-[10px] text-slate-500 mt-0.5 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatShortDate(task.due_datetime)} {formatTime(task.due_datetime)}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {!task.converted_team_task_id && task.status === "open" && (
                      <Button variant="ghost" size="sm" onClick={() => onConvertToTeamTask(task.id)} className="h-6 w-6 p-0" title={t("pulsePlanner.convertToTeamTask")}>
                        <Users className="w-3 h-3" />
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => onEditTask(task)} className="h-6 w-6 p-0">
                      <Edit className="w-3 h-3" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => onDeleteTask(task.id)} className="h-6 w-6 p-0 text-red-600">
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};
